import { Injectable } from '@angular/core';

import { ApiCredentials } from '../models/api-profile.model';

@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private readonly storagePrefix = 'api_workspace_credentials';

  getCredentials(profileId: string): ApiCredentials {
    const rawValue = localStorage.getItem(this.getStorageKey(profileId));

    if (rawValue === null) {
      return {};
    }

    try {
      const parsed: unknown = JSON.parse(rawValue);
      return this.isCredentials(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  saveBearerToken(profileId: string, value: string): void {
    this.saveCredentials(profileId, {
      ...this.getCredentials(profileId),
      bearerToken: this.normalizeToken(value),
    });
  }

  saveApiKey(profileId: string, value: string): void {
    this.saveCredentials(profileId, {
      ...this.getCredentials(profileId),
      apiKey: value.trim(),
    });
  }

  clearCredentials(profileId: string): void {
    localStorage.removeItem(this.getStorageKey(profileId));
  }

  hasCredentials(profileId: string): boolean {
    const credentials = this.getCredentials(profileId);
    return (
      (credentials.bearerToken?.length ?? 0) > 0 ||
      (credentials.apiKey?.length ?? 0) > 0
    );
  }

  normalizeToken(value: string): string {
    return value.trim().replace(/^Bearer\s+/i, '').trim();
  }

  private saveCredentials(
    profileId: string,
    credentials: ApiCredentials,
  ): void {
    localStorage.setItem(
      this.getStorageKey(profileId),
      JSON.stringify(credentials),
    );
  }

  private getStorageKey(profileId: string): string {
    return `${this.storagePrefix}:${profileId}`;
  }

  private isCredentials(value: unknown): value is ApiCredentials {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return false;
    }

    const record = value as Record<string, unknown>;
    return (
      (record['bearerToken'] === undefined ||
        typeof record['bearerToken'] === 'string') &&
      (record['apiKey'] === undefined || typeof record['apiKey'] === 'string')
    );
  }
}
